import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { CSpinner } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft, cilArrowRight, cilCheck, cilTag, cilTrash, cilX } from '@coreui/icons'
import { post as apiPost, del } from '../../utils/apiClient'
import TagSelector from './TagSelector'
import './PostLightbox.css'

// Un geste horizontal ne change de photo que s'il est franc ET majoritairement
// horizontal : sinon un scroll ou un appui sur un contrôle ferait défiler.
const SWIPE_MIN_DISTANCE = 50
const SWIPE_DOWN_TO_CLOSE = 80

/**
 * Visionneuse plein écran des photos d'un post : navigation, identification.
 *
 * Propriétaire de son index — le parent ne pilote que le point d'entrée
 * (`initialIndex`). Changer de photo ne remonte donc pas le composant : l'état
 * de navigation, le préchargement et le focus survivent au déplacement.
 *
 * Les identifications arrivent du payload du feed via `tagsByImage` ; toute
 * mutation patche l'état local et remonte au parent par `onTagsChange`.
 */
const PostLightbox = ({
  images = [],
  initialIndex = 0,
  postId,
  tagsByImage = {},
  isPostAuthor = false,
  isAdmin = false,
  onTagsChange,
  onClose,
}) => {
  const { t } = useTranslation()

  const [index, setIndex] = useState(initialIndex)
  // Copie locale seedée au montage : tant que la visionneuse est ouverte,
  // c'est elle la source de vérité (cf. onTagsChange pour la remontée).
  const [tagsByIndex, setTagsByIndex] = useState(tagsByImage)
  const [natural, setNatural] = useState(null)
  const [frame, setFrame] = useState(null)
  const [tagMode, setTagMode] = useState(false)
  const [tempTag, setTempTag] = useState(null)
  const [hoveredTag, setHoveredTag] = useState(null)
  // Les noms sont visibles par défaut : une identification qui ne se découvre
  // qu'au survol n'existe pas au doigt. Le bouton ne fait que les masquer.
  const [showNames, setShowNames] = useState(true)

  const rootRef = useRef(null)
  const stageRef = useRef(null)
  const selectorRef = useRef(null)
  const touchStartRef = useRef(null)

  const currentUserId = useMemo(() => parseInt(localStorage.getItem('userId'), 10), [])

  const total = images.length
  const currentImage = images[index]
  const tags = tagsByIndex[index] || []
  const hasPrev = index > 0
  const hasNext = index < total - 1

  // ── Navigation ────────────────────────────────────────────────────────

  // Changer de photo sort du mode identification : un clic destiné à la photo
  // précédente ne doit pas poser une pastille sur la suivante.
  const goTo = useCallback(
    (nextIndex) => {
      if (nextIndex < 0 || nextIndex > total - 1) return
      setIndex(nextIndex)
      setNatural(null)
      setFrame(null)
      setTempTag(null)
      setTagMode(false)
      setHoveredTag(null)
    },
    [total],
  )

  const goPrev = useCallback(() => goTo(index - 1), [goTo, index])
  const goNext = useCallback(() => goTo(index + 1), [goTo, index])

  const exitTagMode = useCallback(() => {
    setTagMode(false)
    setTempTag(null)
  }, [])

  // ── Cadre réel de la photo ────────────────────────────────────────────

  // On calcule nous-mêmes la boîte occupée par l'image (contain, sans
  // agrandissement au-delà de sa taille naturelle) : c'est le repère exact des
  // pastilles, qui peuvent alors être posées en pourcentages bruts.
  const computeFrame = useCallback(() => {
    const stage = stageRef.current
    if (!stage || !natural) return

    const { width: stageWidth, height: stageHeight } = stage.getBoundingClientRect()
    if (!stageWidth || !stageHeight) return

    const scale = Math.min(stageWidth / natural.width, stageHeight / natural.height, 1)
    const width = natural.width * scale
    const height = natural.height * scale

    setFrame({
      left: (stageWidth - width) / 2,
      top: (stageHeight - height) / 2,
      width,
      height,
    })
  }, [natural])

  useLayoutEffect(() => {
    computeFrame()
  }, [computeFrame])

  // Rotation, redimensionnement, ouverture/fermeture du clavier virtuel :
  // le cadre se recalcule, les pastilles suivent.
  useEffect(() => {
    const stage = stageRef.current
    if (!stage || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => computeFrame())
    observer.observe(stage)
    return () => observer.disconnect()
  }, [computeFrame])

  const handleImageLoad = useCallback((event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget
    if (!naturalWidth || !naturalHeight) return
    setNatural({ width: naturalWidth, height: naturalHeight })
  }, [])

  // ── Préchargement des voisines ────────────────────────────────────────

  useEffect(() => {
    ;[index - 1, index + 1].forEach((neighbour) => {
      const url = images[neighbour]
      if (!url) return
      const preloader = new Image()
      preloader.src = url
    })
  }, [index, images])

  // ── Clavier, verrou de scroll, restitution du focus ───────────────────

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Échap remonte d'un cran à la fois : sélecteur, puis mode
        // identification, puis la visionneuse.
        if (tempTag) setTempTag(null)
        else if (tagMode) exitTagMode()
        else onClose()
        return
      }

      // Les flèches appartiennent au champ de saisie quand on tape : sans ce
      // garde, naviguer dans la recherche du sélecteur changerait de photo.
      const target = event.target
      const isTyping =
        target instanceof HTMLElement &&
        (target.matches('input, textarea, select') || target.isContentEditable)

      if (isTyping) return

      if (event.key === 'ArrowLeft') {
        goPrev()
      } else if (event.key === 'ArrowRight') {
        goNext()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [tagMode, tempTag, exitTagMode, onClose, goPrev, goNext])

  useEffect(() => {
    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow

    document.body.style.overflow = 'hidden'
    rootRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      // Le déclencheur retrouve le focus : au clavier, on revient sur la
      // vignette qu'on venait d'ouvrir, pas en haut du document.
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
  }, [])

  // Fermeture du sélecteur au clic extérieur.
  useEffect(() => {
    if (!tempTag) return

    const handleOutsideClick = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setTempTag(null)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [tempTag])

  // ── Gestes tactiles ───────────────────────────────────────────────────

  const handleTouchStart = (event) => {
    // La bande de miniatures défile horizontalement : un geste qui y démarre
    // lui appartient, il ne doit pas être lu comme un changement de photo.
    if (tagMode || event.target.closest('.aw-lightbox__thumbs')) return
    const touch = event.touches[0]
    touchStartRef.current = { x: touch.clientX, y: touch.clientY }
  }

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current
    touchStartRef.current = null
    if (!start || tagMode) return

    const touch = event.changedTouches[0]
    const dx = touch.clientX - start.x
    const dy = touch.clientY - start.y

    if (Math.abs(dx) > SWIPE_MIN_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext()
      else goPrev()
    } else if (dy > SWIPE_DOWN_TO_CLOSE && Math.abs(dy) > Math.abs(dx)) {
      onClose()
    }
  }

  // ── Identification ────────────────────────────────────────────────────

  const commitTags = (nextTags) => {
    setTagsByIndex((prev) => ({ ...prev, [index]: nextTags }))
    if (onTagsChange) onTagsChange(index, nextTags)
  }

  // Cliquer le fond (hors de la photo) ferme, sauf en identification où un
  // clic à côté du cadre est le plus souvent une visée manquée.
  const handleStageClick = (event) => {
    if (tagMode) return
    if (event.target === event.currentTarget) onClose()
  }

  // Le cadre EST l'image : le pourcentage se lit directement, aucune
  // correction de letterboxing à appliquer.
  const handleFrameClick = (event) => {
    if (!tagMode) return
    // Un clic sur une pastille existante (ou sur le sélecteur ouvert) n'est pas
    // une demande de nouvelle identification — sans ce garde, supprimer un tag
    // rouvrait aussitôt le sélecteur au même endroit.
    if (event.target.closest('.aw-lightbox__tag, .aw-lightbox__selector')) return

    const rect = event.currentTarget.getBoundingClientRect()
    setTempTag({
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100,
    })
  }

  const handleSelectPerson = async (member) => {
    if (!tempTag) return

    try {
      const created = await apiPost(`/posts/${postId}/tags`, {
        member_id: member.id,
        position_x: tempTag.x,
        position_y: tempTag.y,
        image_index: index,
      })

      // Le serveur repositionne au lieu de dupliquer si le membre était déjà
      // identifié sur cette photo : on remplace l'entrée de même id.
      commitTags([...tags.filter((tag) => tag.id !== created.id), created])
      setTempTag(null)
    } catch (error) {
      console.error("Erreur lors de l'ajout du tag:", error)
    }
  }

  const handleDeleteTag = async (tagId, event) => {
    event?.stopPropagation()
    event?.preventDefault()

    try {
      await del(`/posts/tags/${tagId}`)
      commitTags(tags.filter((tag) => tag.id !== tagId))
    } catch (error) {
      console.error('Erreur lors de la suppression du tag:', error)
    }
  }

  if (!currentImage) return null

  // Miroir exact de la politique du backend (postTagController). Le front ne
  // fait qu'afficher ou masquer : l'autorisation réelle est côté serveur.
  const canTag = isPostAuthor || isAdmin
  const canDeleteTag = (tag) =>
    isPostAuthor || isAdmin || tag.created_by === currentUserId || tag.member_id === currentUserId

  return createPortal(
    <div
      ref={rootRef}
      className="aw-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={t('posts:lightbox.dialogLabel')}
      tabIndex={-1}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="aw-lightbox__bar">
        <div className="d-flex align-items-center gap-2">
          {tagMode ? (
            <button type="button" className="btn btn-success btn-sm" onClick={exitTagMode}>
              <CIcon icon={cilCheck} className="me-1" />
              {t('posts:tagging.finish')}
            </button>
          ) : (
            canTag && (
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => setTagMode(true)}
              >
                <CIcon icon={cilTag} className="me-1" />
                {t('posts:tagging.identifyPeople')}
              </button>
            )
          )}

          {tags.length > 0 && !tagMode && (
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={() => setShowNames((prev) => !prev)}
              aria-pressed={!showNames}
            >
              {showNames ? t('posts:tagging.hideNames') : t('posts:tagging.showNames')}
            </button>
          )}
        </div>

        <span className="aw-lightbox__counter">
          {t('posts:lightbox.counter', { current: index + 1, total })}
        </span>

        <button
          type="button"
          className="btn btn-sm btn-outline-light"
          onClick={onClose}
          aria-label={t('common:close')}
        >
          <CIcon icon={cilX} />
        </button>
      </div>

      {tagMode && <div className="aw-lightbox__hint">{t('posts:tagging.clickToIdentify')}</div>}

      <div className="aw-lightbox__stage" ref={stageRef} onClick={handleStageClick}>
        {total > 1 && (
          <button
            type="button"
            className="aw-lightbox__nav aw-lightbox__nav--prev"
            onClick={goPrev}
            disabled={!hasPrev}
            aria-label={t('posts:lightbox.previous')}
          >
            <CIcon icon={cilArrowLeft} size="lg" />
          </button>
        )}

        {!frame && <CSpinner className="aw-lightbox__spinner" />}

        <div
          className={`aw-lightbox__frame ${tagMode ? 'aw-lightbox__frame--tagging' : ''}`}
          style={
            frame
              ? {
                  left: `${frame.left}px`,
                  top: `${frame.top}px`,
                  width: `${frame.width}px`,
                  height: `${frame.height}px`,
                }
              : { visibility: 'hidden', inset: 0 }
          }
          onClick={handleFrameClick}
        >
          <img
            key={currentImage}
            src={currentImage}
            alt={t('posts:media.enlargedAlt')}
            className="aw-lightbox__image"
            onLoad={handleImageLoad}
          />

          <div className="aw-lightbox__tags">
            {tags.map((tag) => (
              <div
                key={tag.id}
                className="aw-lightbox__tag"
                style={{ left: `${tag.position_x}%`, top: `${tag.position_y}%` }}
                onMouseEnter={() => setHoveredTag(tag.id)}
                onMouseLeave={() => setHoveredTag(null)}
                // Au doigt il n'y a pas de survol : le tap révèle le nom, sinon
                // les identifications resteraient invisibles sur mobile hors
                // mode édition.
                onClick={() => setHoveredTag((prev) => (prev === tag.id ? null : tag.id))}
              >
                <span className="aw-lightbox__dot" />
                {(showNames || tagMode || hoveredTag === tag.id) && (
                  <span className="aw-lightbox__label">
                    {tag.member.firstName} {tag.member.lastName}
                    {/* Non conditionné au mode identification : la personne
                        identifiée a le droit de se retirer, or elle n'a pas
                        accès à ce mode si elle n'est ni auteur ni admin. */}
                    {canDeleteTag(tag) && (
                      <button
                        type="button"
                        className="aw-lightbox__delete"
                        onClick={(event) => handleDeleteTag(tag.id, event)}
                        aria-label={t('posts:lightbox.removeTag')}
                      >
                        <CIcon icon={cilTrash} size="sm" />
                      </button>
                    )}
                  </span>
                )}
              </div>
            ))}

            {tempTag && (
              <div
                className="aw-lightbox__tag"
                style={{ left: `${tempTag.x}%`, top: `${tempTag.y}%` }}
              >
                <span className="aw-lightbox__dot aw-lightbox__dot--temp" />
              </div>
            )}
          </div>

          {tempTag && (
            <div
              ref={selectorRef}
              className="aw-lightbox__selector"
              // Position passée en variables et non en left/top directs : un
              // style en ligne battrait la media query qui bascule le sélecteur
              // en feuille bas d'écran sur mobile.
              style={{
                '--aw-selector-x': `${tempTag.x}%`,
                '--aw-selector-y': `calc(${tempTag.y}% + 1.75rem)`,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">{t('posts:tagging.identifyPerson')}</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() => setTempTag(null)}
                  aria-label={t('common:close')}
                >
                  <CIcon icon={cilX} />
                </button>
              </div>
              <TagSelector onSelectPerson={handleSelectPerson} />
            </div>
          )}
        </div>

        {total > 1 && (
          <button
            type="button"
            className="aw-lightbox__nav aw-lightbox__nav--next"
            onClick={goNext}
            disabled={!hasNext}
            aria-label={t('posts:lightbox.next')}
          >
            <CIcon icon={cilArrowRight} size="lg" />
          </button>
        )}
      </div>

      {/* La bande de miniatures vit ici et nulle part ailleurs : dans le fil,
          elle faisait doublon avec la grille. */}
      {total > 4 && (
        <div className="aw-lightbox__thumbs">
          {images.map((image, thumbIndex) => (
            <button
              type="button"
              key={`${image}-${thumbIndex}`}
              className={`aw-lightbox__thumb ${
                thumbIndex === index ? 'aw-lightbox__thumb--active' : ''
              }`}
              onClick={() => goTo(thumbIndex)}
              aria-label={t('posts:media.thumbnailAlt', { index: thumbIndex + 1 })}
              aria-current={thumbIndex === index}
            >
              <img src={image} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  )
}

export default PostLightbox
